import { __decorate, __metadata } from "tslib";
import { changedAttribute, elementDefine, eventShadow, mutationObserverLight, onConnectedAfter, onConnectedBodyShadow, queryShadow, resizeObserverLight } from "@dooboostore/simple-web-component";
const tagName = "cartesian-chart";
function num(v, def) {
  if (v == null || v.trim() === "")
    return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function str(v) {
  if (v == null)
    return void 0;
  const t = v.trim();
  return t === "" ? void 0 : t;
}
function fmtNum(v) {
  if (v === 0)
    return "0";
  const abs = Math.abs(v);
  if (abs >= 100)
    return v.toFixed(0);
  if (abs >= 1)
    return String(parseFloat(v.toFixed(2)));
  return String(parseFloat(v.toPrecision(3)));
}
function parseDash(dash) {
  if (!dash)
    return [];
  return dash.split(/[,\s]+/).map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
}
var CartesianChart_default = (w) => {
  var _a, _b, _c, _d, _e, _f;
  const existing = w.customElements.get(tagName);
  if (existing)
    return existing;
  let CartesianChartImpl = class CartesianChartImpl extends w.HTMLElement {
    constructor() {
      super(...arguments);
      Object.defineProperty(this, "series", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "polygons", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "vectors", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "markers", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "circles", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "arcs", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "scaleX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 1
      });
      Object.defineProperty(this, "scaleY", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 1
      });
      Object.defineProperty(this, "offsetX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "offsetY", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchDist0", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchScaleX0", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 1
      });
      Object.defineProperty(this, "dragging", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "dragLastX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "dragLastY", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "resizeRaf", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "canvas", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: void 0
      });
    }
    onXMinChanged() {
      if (this.canvas)
        this.draw();
    }
    onXMaxChanged() {
      if (this.canvas)
        this.draw();
    }
    onYMinChanged() {
      if (this.canvas)
        this.draw();
    }
    onYMaxChanged() {
      if (this.canvas)
        this.draw();
    }
    onXLabelChanged() {
      if (this.canvas)
        this.draw();
    }
    onYLabelChanged() {
      if (this.canvas)
        this.draw();
    }
    onCenterXChanged() {
      if (this.canvas)
        this.draw();
    }
    onCenterYChanged() {
      if (this.canvas)
        this.draw();
    }
    setData(data) {
      this.series = data.series || [];
      this.polygons = data.polygons || [];
      this.vectors = data.vectors || [];
      this.markers = data.markers || [];
      this.circles = data.circles || [];
      this.arcs = data.arcs || [];
      this.resetView();
      if (this.isConnected && this.canvas)
        this.draw();
    }
    reset() {
      this.resetView();
      if (this.canvas)
        this.draw();
    }
    resetView() {
      this.scaleX = 1;
      this.scaleY = 1;
      this.offsetX = 0;
      this.offsetY = 0;
    }
    // ── 데이터 수집 ──
    collect() {
      const series = [];
      this.querySelectorAll(":scope > series").forEach((el) => {
        const raw = (el.getAttribute("points") || "").trim();
        if (!raw)
          return;
        const points = [];
        raw.split(/[\s;|]+/).forEach((pair) => {
          const [sx, sy] = pair.split(",");
          const x = Number(sx), y = Number(sy);
          if (Number.isFinite(x) && Number.isFinite(y))
            points.push({ x, y });
        });
        if (!points.length)
          return;
        series.push({
          points,
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 2),
          dash: str(el.getAttribute("dash") || el.getAttribute("line-dash")),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("label-color"))
        });
      });
      this.series = series;
      const polygons = [];
      this.querySelectorAll(":scope > polygon").forEach((el) => {
        const raw = (el.getAttribute("points") || "").trim();
        if (!raw)
          return;
        const points = [];
        raw.split(/[\s;|]+/).forEach((pair) => {
          const [sx, sy] = pair.split(",");
          const x = Number(sx), y = Number(sy);
          if (Number.isFinite(x) && Number.isFinite(y))
            points.push({ x, y });
        });
        if (points.length < 3)
          return;
        polygons.push({
          points,
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 1.5),
          dash: str(el.getAttribute("dash") || el.getAttribute("line-dash")),
          fill: str(el.getAttribute("fill") || el.getAttribute("fill-style")),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("label-color"))
        });
      });
      this.polygons = polygons;
      const vectors = [];
      this.querySelectorAll(":scope > vector").forEach((el) => {
        const x1 = Number(el.getAttribute("x1")), y1 = Number(el.getAttribute("y1"));
        const x2 = Number(el.getAttribute("x2")), y2 = Number(el.getAttribute("y2"));
        if (![x1, y1, x2, y2].every(Number.isFinite))
          return;
        vectors.push({
          x1,
          y1,
          x2,
          y2,
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 2),
          dash: str(el.getAttribute("dash") || el.getAttribute("line-dash")),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("labelColor") || el.getAttribute("label-color"))
        });
      });
      this.vectors = vectors;
      const markers = [];
      this.querySelectorAll(":scope > marker").forEach((el) => {
        const x = Number(el.getAttribute("x")), y = Number(el.getAttribute("y"));
        if (!Number.isFinite(x) || !Number.isFinite(y))
          return;
        markers.push({
          x,
          y,
          color: str(el.getAttribute("color")) || "#10b981",
          size: num(el.getAttribute("size"), 4),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("labelColor") || el.getAttribute("label-color"))
        });
      });
      this.markers = markers;
      const circles = [];
      this.querySelectorAll(":scope > circle").forEach((el) => {
        const x = Number(el.getAttribute("x")), y = Number(el.getAttribute("y")), r = Number(el.getAttribute("r"));
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(r) || r <= 0)
          return;
        const ryRaw = el.getAttribute("ry");
        circles.push({
          x,
          y,
          r,
          ry: ryRaw != null && ryRaw.trim() !== "" && Number.isFinite(Number(ryRaw)) ? Number(ryRaw) : void 0,
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 2),
          dash: str(el.getAttribute("dash") || el.getAttribute("line-dash")),
          fill: str(el.getAttribute("fill") || el.getAttribute("fill-style")),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("labelColor") || el.getAttribute("label-color"))
        });
      });
      this.circles = circles;
      const arcs = [];
      this.querySelectorAll(":scope > arc").forEach((el) => {
        const x = Number(el.getAttribute("x")), y = Number(el.getAttribute("y")), r = Number(el.getAttribute("r"));
        const s = Number(el.getAttribute("start-angle")), e = Number(el.getAttribute("end-angle"));
        if (![x, y, r, s, e].every(Number.isFinite) || r <= 0)
          return;
        arcs.push({
          x,
          y,
          r,
          startAngle: s,
          endAngle: e,
          color: str(el.getAttribute("color")) || "#10b981",
          width: num(el.getAttribute("width"), 1.6)
        });
      });
      this.arcs = arcs;
    }
    onConnected() {
      this.collect();
      this.resetView();
      if (this.canvas)
        this.draw();
    }
    onMutated() {
      this.collect();
      if (this.canvas)
        this.draw();
    }
    onResize() {
      if (!this.canvas)
        return;
      if (this.resizeRaf)
        cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        if (this.canvas)
          this.draw();
      });
    }
    render() {
      return `
        <style>
          :host { display:block; position:relative; height:var(--cc-canvas-height, 320px); }
          #cc-canvas {
            display:block; width:100%; height:100%;
            touch-action:pan-y; cursor:crosshair;
            background:#fff;
          }
          :host([disabled-zoom]) #cc-canvas { cursor:default; }
          #cc-reset {
            position:absolute; top:6px; right:6px; z-index:2;
            width:26px; height:26px; border-radius:6px;
            border:1px solid #e2e8f0; background:rgba(255,255,255,0.92);
            color:#64748b; font-size:13px; cursor:pointer; line-height:1; padding:0;
          }
          #cc-reset:hover { background:#f1f5f9; }
        </style>
        <canvas id="cc-canvas"></canvas>
        <button id="cc-reset" title="\uBCF4\uAE30 \uCD08\uAE30\uD654">\u27F2</button>
      `;
    }
    // ── 범위 ──
    getBaseBounds() {
      const explicit = (name) => {
        const v = this.getAttribute(name);
        if (v == null || v.trim() === "" || v.trim().toLowerCase() === "auto")
          return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const exL = explicit("x-min"), exR = explicit("x-max");
      const eyB = explicit("y-min"), eyT = explicit("y-max");
      const xs = [], ys = [];
      this.series.forEach((s) => s.points.forEach((p) => {
        xs.push(p.x);
        ys.push(p.y);
      }));
      this.polygons.forEach((s) => s.points.forEach((p) => {
        xs.push(p.x);
        ys.push(p.y);
      }));
      this.vectors.forEach((v) => {
        xs.push(v.x1, v.x2);
        ys.push(v.y1, v.y2);
      });
      this.markers.forEach((m) => {
        xs.push(m.x);
        ys.push(m.y);
      });
      this.circles.forEach((c) => {
        xs.push(c.x - c.r, c.x + c.r);
        ys.push(c.y - (c.ry ?? c.r), c.y + (c.ry ?? c.r));
      });
      this.arcs.forEach((a) => {
        xs.push(a.x);
        ys.push(a.y);
      });
      let axL, axR, ayB, ayT;
      if (!xs.length) {
        axL = -6;
        axR = 6;
        ayB = -4;
        ayT = 4;
      } else {
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const xPad = (maxX - minX) * 0.18 || Math.abs(maxX) * 0.18 || 1;
        const yPad = (maxY - minY) * 0.18 || Math.abs(maxY) * 0.18 || 1;
        axL = minX - xPad;
        axR = maxX + xPad;
        ayB = minY - yPad;
        ayT = maxY + yPad;
      }
      const centerNum = (name) => {
        const v = this.getAttribute(name);
        if (v == null || v.trim() === "")
          return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const ccx = centerNum("center-x"), ccy = centerNum("center-y");
      let xL = exL, xR = exR, yB = eyB, yT = eyT;
      if (ccx != null) {
        const half = Math.max(Math.abs(axR - ccx), Math.abs(axL - ccx));
        if (xL == null)
          xL = ccx - half;
        if (xR == null)
          xR = ccx + half;
      }
      if (xL == null)
        xL = axL;
      if (xR == null)
        xR = axR;
      if (ccy != null) {
        const half = Math.max(Math.abs(ayT - ccy), Math.abs(ayB - ccy));
        if (yB == null)
          yB = ccy - half;
        if (yT == null)
          yT = ccy + half;
      }
      if (yB == null)
        yB = ayB;
      if (yT == null)
        yT = ayT;
      if (!(xR > xL)) {
        xL = axL;
        xR = axR;
      }
      if (!(yT > yB)) {
        yB = ayB;
        yT = ayT;
      }
      return { xL, xR, yB, yT };
    }
    getPlotRect() {
      const canvas = this.canvas;
      if (!canvas)
        return null;
      const cssW = canvas.clientWidth || 400;
      const cssH = canvas.clientHeight || 340;
      const padL = 46, padR = 14, padT = 12, padB = 30;
      const plotW = Math.max(10, cssW - padL - padR);
      const plotH = Math.max(10, cssH - padT - padB);
      return { padL, padR, padT, padB, plotW, plotH, cssW, cssH };
    }
    isInPlot(clientX, clientY) {
      const rect = this.canvas?.getBoundingClientRect();
      const pr = this.getPlotRect();
      if (!rect || !pr)
        return false;
      const x = clientX - rect.left, y = clientY - rect.top;
      return x >= pr.padL && x <= pr.padL + pr.plotW && y >= pr.padT && y <= pr.padT + pr.plotH;
    }
    zoomAt(factor, fracX, fracY) {
      const b = this.getBaseBounds();
      const xRange0 = b.xR - b.xL, yRange0 = b.yT - b.yB;
      const dampFactor = 1 + (factor - 1) * 0.65;
      const newSX = Math.min(50, Math.max(0.2, this.scaleX * dampFactor));
      const newSY = Math.min(50, Math.max(0.2, this.scaleY * dampFactor));
      this.offsetX += xRange0 * fracX * (1 / this.scaleX - 1 / newSX);
      this.offsetY += yRange0 * fracY * (1 / this.scaleY - 1 / newSY);
      this.scaleX = newSX;
      this.scaleY = newSY;
    }
    haloText(ctx, text, x, y) {
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 3;
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
      ctx.restore();
    }
    // ── 드로잉 ──
    draw() {
      const canvas = this.canvas;
      if (!canvas)
        return;
      const dpr = w.devicePixelRatio || 1;
      const pr = this.getPlotRect();
      if (!pr)
        return;
      const { padL, padR, padT, padB, plotW, plotH, cssW, cssH } = pr;
      if (cssW < 40 || cssH < 40)
        return;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx)
        return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const b = this.getBaseBounds();
      const xRange = (b.xR - b.xL) / this.scaleX;
      const yRange = (b.yT - b.yB) / this.scaleY;
      let xl = b.xL + this.offsetX;
      let xr = xl + xRange;
      let yb = b.yB + this.offsetY;
      let yt = yb + yRange;
      if (!this.hasAttribute("disabled-aspect")) {
        const xPerPx = (xr - xl) / plotW;
        const yPerPx = (yt - yb) / plotH;
        if (xPerPx > yPerPx) {
          const midY = (yb + yt) / 2;
          const newYRange = xPerPx * plotH;
          yb = midY - newYRange / 2;
          yt = midY + newYRange / 2;
        } else if (yPerPx > xPerPx) {
          const midX = (xl + xr) / 2;
          const newXRange = yPerPx * plotW;
          xl = midX - newXRange / 2;
          xr = midX + newXRange / 2;
        }
      }
      const toX = (v) => padL + (v - xl) / (xr - xl) * plotW;
      const toY = (v) => padT + plotH - (v - yb) / (yt - yb) * plotH;
      const xDataPerPx = (xr - xl) / plotW;
      const yDataPerPx = (yt - yb) / plotH;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cssW, cssH);
      const showGrid = !this.hasAttribute("hide-grid");
      const gridColor = "#e2e8f0";
      const niceStep = (range) => {
        const raw = range / 4;
        const mag = Math.pow(10, Math.floor(Math.log10(raw)));
        const norm = raw / mag;
        return (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
      };
      ctx.font = "9px -apple-system,sans-serif";
      const yStep = niceStep(yt - yb);
      const yStart = Math.ceil(yb / yStep) * yStep;
      const yCount = Math.floor((yt - yStart) / yStep);
      for (let k = 0; k <= yCount; k++) {
        const yv = Math.round((yStart + k * yStep) * 1e9) / 1e9;
        const gy = toY(yv);
        if (gy < padT - 4 || gy > cssH - padB + 4)
          continue;
        if (showGrid) {
          ctx.strokeStyle = gridColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(padL, gy);
          ctx.lineTo(cssW - padR, gy);
          ctx.stroke();
        }
        ctx.fillStyle = "#94a3b8";
        ctx.textAlign = "right";
        ctx.fillText(fmtNum(yv), padL - 4, gy + 3);
      }
      ctx.fillStyle = "#94a3b8";
      ctx.textAlign = "center";
      const xStep = niceStep(xr - xl);
      const xStart = Math.ceil(xl / xStep) * xStep;
      const xCount = Math.floor((xr - xStart) / xStep);
      for (let k = 0; k <= xCount; k++) {
        const xv = Math.round((xStart + k * xStep) * 1e9) / 1e9;
        const gx = toX(xv);
        if (gx < padL || gx > cssW - padR)
          continue;
        if (showGrid) {
          ctx.strokeStyle = gridColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(gx, padT);
          ctx.lineTo(gx, cssH - padB);
          ctx.stroke();
        }
        ctx.fillText(fmtNum(xv), gx, cssH - padB + 13);
      }
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 1.2;
      if (yb <= 0 && yt >= 0) {
        const zy = toY(0);
        ctx.beginPath();
        ctx.moveTo(padL, zy);
        ctx.lineTo(cssW - padR, zy);
        ctx.stroke();
      }
      if (xl <= 0 && xr >= 0) {
        const zx = toX(0);
        ctx.beginPath();
        ctx.moveTo(zx, padT);
        ctx.lineTo(zx, cssH - padB);
        ctx.stroke();
      }
      const xLabel = this.getAttribute("x-label") || "";
      const yLabel = this.getAttribute("y-label") || "";
      if (xLabel || yLabel) {
        ctx.fillStyle = "#64748b";
        ctx.font = "bold 9px -apple-system,sans-serif";
        ctx.textAlign = "center";
        if (xLabel)
          ctx.fillText(xLabel, padL + plotW / 2, cssH - 4);
        if (yLabel) {
          ctx.save();
          ctx.translate(10, padT + plotH / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(yLabel, 0, 0);
          ctx.restore();
        }
      }
      ctx.save();
      ctx.beginPath();
      ctx.rect(padL, padT, plotW, plotH);
      ctx.clip();
      for (const s of this.series) {
        ctx.save();
        ctx.strokeStyle = s.color || "#6366f1";
        ctx.lineWidth = s.width ?? 2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        const dash = parseDash(s.dash);
        if (dash.length)
          ctx.setLineDash(dash);
        ctx.beginPath();
        let started = false;
        for (const p of s.points) {
          if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) {
            started = false;
            continue;
          }
          const px = toX(p.x), py = toY(p.y);
          if (!started) {
            ctx.moveTo(px, py);
            started = true;
          } else
            ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.restore();
        if (s.label && s.points.length) {
          const last = s.points[s.points.length - 1];
          ctx.fillStyle = s.labelColor || s.color || "#6366f1";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "left";
          this.haloText(ctx, s.label, toX(last.x) + 6, toY(last.y) - 6);
        }
      }
      for (const p of this.polygons) {
        ctx.save();
        ctx.beginPath();
        p.points.forEach((pt, k) => {
          const px = toX(pt.x), py = toY(pt.y);
          if (k === 0)
            ctx.moveTo(px, py);
          else
            ctx.lineTo(px, py);
        });
        ctx.closePath();
        if (p.fill) {
          ctx.fillStyle = p.fill;
          ctx.fill();
        }
        ctx.strokeStyle = p.color || "#6366f1";
        ctx.lineWidth = p.width ?? 1.5;
        const dash = parseDash(p.dash);
        if (dash.length)
          ctx.setLineDash(dash);
        ctx.stroke();
        ctx.restore();
        if (p.label && p.points.length) {
          const cx = p.points.reduce((s, pt) => s + pt.x, 0) / p.points.length;
          const cy = p.points.reduce((s, pt) => s + pt.y, 0) / p.points.length;
          ctx.fillStyle = p.labelColor || p.color || "#6366f1";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "center";
          this.haloText(ctx, p.label, toX(cx), toY(cy));
        }
      }
      for (const c of this.circles) {
        const cx = toX(c.x), cy = toY(c.y);
        const rxPx = c.r / xDataPerPx;
        const ryPx = (c.ry ?? c.r * xDataPerPx / yDataPerPx) / yDataPerPx;
        ctx.save();
        if (c.fill) {
          ctx.fillStyle = c.fill;
          ctx.beginPath();
          ctx.ellipse(cx, cy, rxPx, ryPx, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.strokeStyle = c.color || "#6366f1";
        ctx.lineWidth = c.width ?? 2;
        const dash = parseDash(c.dash);
        if (dash.length)
          ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.ellipse(cx, cy, rxPx, ryPx, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        if (c.label) {
          ctx.fillStyle = c.labelColor || c.color || "#6366f1";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "left";
          this.haloText(ctx, c.label, cx + rxPx + 4, cy - 4);
        }
      }
      for (const v of this.vectors) {
        const x1 = toX(v.x1), y1 = toY(v.y1), x2 = toX(v.x2), y2 = toY(v.y2);
        ctx.save();
        ctx.strokeStyle = v.color || "#6366f1";
        ctx.fillStyle = v.color || "#6366f1";
        ctx.lineWidth = v.width ?? 2;
        const dash = parseDash(v.dash);
        if (dash.length)
          ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.setLineDash([]);
        const ang = Math.atan2(y2 - y1, x2 - x1);
        const h = 8;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - h * Math.cos(ang - 0.4), y2 - h * Math.sin(ang - 0.4));
        ctx.lineTo(x2 - h * Math.cos(ang + 0.4), y2 - h * Math.sin(ang + 0.4));
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        if (v.label) {
          ctx.fillStyle = v.labelColor || v.color || "#6366f1";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "left";
          this.haloText(ctx, v.label, x2 + 6, y2 - 6);
        }
      }
      for (const m of this.markers) {
        const px = toX(m.x), py = toY(m.y);
        ctx.save();
        ctx.fillStyle = m.color || "#10b981";
        ctx.beginPath();
        ctx.arc(px, py, m.size ?? 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (m.label) {
          ctx.fillStyle = m.labelColor || m.color || "#10b981";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "left";
          this.haloText(ctx, m.label, px + 7, py - 6);
        }
      }
      for (const a of this.arcs) {
        ctx.save();
        ctx.strokeStyle = a.color || "#10b981";
        ctx.lineWidth = a.width ?? 1.6;
        ctx.beginPath();
        ctx.arc(toX(a.x), toY(a.y), a.r, -a.startAngle, -a.endAngle, a.startAngle < a.endAngle);
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
    }
    // ── 이벤트: 휠 줌 ──
    onWheel(e) {
      if (!!this.hasAttribute("disabled-zoom"))
        return;
      if (!this.isInPlot(e.clientX, e.clientY))
        return;
      e.preventDefault();
      const canvas = this.canvas;
      if (!canvas)
        return;
      const rect = canvas.getBoundingClientRect();
      const pr = this.getPlotRect();
      if (!pr)
        return;
      const fracX = Math.max(0, Math.min(1, (e.clientX - rect.left - pr.padL) / pr.plotW));
      const fracY = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top - pr.padT) / pr.plotH));
      const delta = Math.max(-120, Math.min(120, e.deltaY));
      const factor = Math.exp(-delta * 55e-5 * 8);
      const clampedFactor = Math.max(0.94, Math.min(1.06, factor));
      this.zoomAt(clampedFactor, fracX, fracY);
      this.draw();
    }
    onMouseDown(e) {
      if (!!this.hasAttribute("disabled-zoom") || !this.isInPlot(e.clientX, e.clientY)) {
        this.dragging = false;
        return;
      }
      this.dragging = true;
      this.dragLastX = e.clientX;
      this.dragLastY = e.clientY;
    }
    onMouseMove(e) {
      if (!this.dragging || !!this.hasAttribute("disabled-zoom"))
        return;
      const pr = this.getPlotRect();
      const b = this.getBaseBounds();
      if (!pr)
        return;
      const dx = -(e.clientX - this.dragLastX) / pr.plotW * ((b.xR - b.xL) / this.scaleX);
      const dy = (e.clientY - this.dragLastY) / pr.plotH * ((b.yT - b.yB) / this.scaleY);
      this.offsetX += dx;
      this.offsetY += dy;
      this.dragLastX = e.clientX;
      this.dragLastY = e.clientY;
      this.draw();
    }
    onMouseUp() {
      this.dragging = false;
    }
    onTouchStart(e) {
      const touches = e.touches;
      if (touches.length === 2 && !this.hasAttribute("disabled-zoom")) {
        const cx = (touches[0].clientX + touches[1].clientX) / 2;
        const cy = (touches[0].clientY + touches[1].clientY) / 2;
        if (!this.isInPlot(cx, cy))
          return;
        e.preventDefault();
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        this.pinchDist0 = Math.hypot(dx, dy);
        this.pinchScaleX0 = this.scaleX;
        this.dragging = false;
      } else if (touches.length === 1) {
        if (!!this.hasAttribute("disabled-zoom") || !this.isInPlot(touches[0].clientX, touches[0].clientY)) {
          this.dragging = false;
          return;
        }
        this.dragging = true;
        this.dragLastX = touches[0].clientX;
        this.dragLastY = touches[0].clientY;
      }
    }
    onTouchMove(e) {
      const touches = e.touches;
      if (touches.length === 2 && this.pinchDist0 > 0 && !this.hasAttribute("disabled-zoom")) {
        e.preventDefault();
        const dist = Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
        if (dist === 0)
          return;
        const cx = (touches[0].clientX + touches[1].clientX) / 2;
        const cy = (touches[0].clientY + touches[1].clientY) / 2;
        const rect = this.canvas.getBoundingClientRect();
        const pr = this.getPlotRect();
        if (!pr || !this.isInPlot(cx, cy))
          return;
        const fracX = Math.max(0, Math.min(1, (cx - rect.left - pr.padL) / pr.plotW));
        const fracY = Math.max(0, Math.min(1, 1 - (cy - rect.top - pr.padT) / pr.plotH));
        const rawFactor = dist / this.pinchDist0;
        const targetSX = Math.min(50, Math.max(0.2, this.pinchScaleX0 * (1 + (rawFactor - 1) * 0.5)));
        this.zoomAt(targetSX / this.scaleX, fracX, fracY);
        this.draw();
      } else if (touches.length === 1 && this.dragging && !this.hasAttribute("disabled-zoom")) {
        if (!this.isInPlot(touches[0].clientX, touches[0].clientY))
          return;
        e.preventDefault();
        const pr = this.getPlotRect();
        const b = this.getBaseBounds();
        if (!pr)
          return;
        const dx = touches[0].clientX - this.dragLastX;
        const dy = touches[0].clientY - this.dragLastY;
        this.offsetX += -dx / pr.plotW * ((b.xR - b.xL) / this.scaleX);
        this.offsetY += dy / pr.plotH * ((b.yT - b.yB) / this.scaleY);
        this.dragLastX = touches[0].clientX;
        this.dragLastY = touches[0].clientY;
        this.draw();
      }
    }
    onTouchEnd() {
      this.pinchDist0 = 0;
      this.dragging = false;
    }
    onResetClick() {
      this.reset();
    }
  };
  __decorate([
    changedAttribute("x-min"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onXMinChanged", null);
  __decorate([
    changedAttribute("x-max"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onXMaxChanged", null);
  __decorate([
    changedAttribute("y-min"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onYMinChanged", null);
  __decorate([
    changedAttribute("y-max"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onYMaxChanged", null);
  __decorate([
    changedAttribute("x-label"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onXLabelChanged", null);
  __decorate([
    changedAttribute("y-label"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onYLabelChanged", null);
  __decorate([
    changedAttribute("center-x"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onCenterXChanged", null);
  __decorate([
    changedAttribute("center-y"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onCenterYChanged", null);
  __decorate([
    onConnectedAfter,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onConnected", null);
  __decorate([
    mutationObserverLight({ childList: true, attributes: true, subtree: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onMutated", null);
  __decorate([
    resizeObserverLight,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onResize", null);
  __decorate([
    onConnectedBodyShadow,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
  ], CartesianChartImpl.prototype, "render", null);
  __decorate([
    queryShadow("#cc-canvas"),
    __metadata("design:type", typeof (_a = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _a : Object)
  ], CartesianChartImpl.prototype, "canvas", void 0);
  __decorate([
    eventShadow("#cc-canvas", "wheel", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_b = typeof WheelEvent !== "undefined" && WheelEvent) === "function" ? _b : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onWheel", null);
  __decorate([
    eventShadow("#cc-canvas", "mousedown"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_c = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _c : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onMouseDown", null);
  __decorate([
    eventShadow("#cc-canvas", "mousemove"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_d = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _d : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onMouseMove", null);
  __decorate([
    eventShadow("#cc-canvas", "mouseup"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onMouseUp", null);
  __decorate([
    eventShadow("#cc-canvas", "touchstart", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_e = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _e : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onTouchStart", null);
  __decorate([
    eventShadow("#cc-canvas", "touchmove", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_f = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _f : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onTouchMove", null);
  __decorate([
    eventShadow("#cc-canvas", "touchend"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onTouchEnd", null);
  __decorate([
    eventShadow("#cc-reset", "click"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChartImpl.prototype, "onResetClick", null);
  CartesianChartImpl = __decorate([
    elementDefine(tagName, { window: w })
  ], CartesianChartImpl);
  return CartesianChartImpl;
};
export {
  CartesianChart_default as default
};
//# sourceMappingURL=CartesianChart.js.map
